import {useCallback, useEffect, useRef} from 'react';
import {getReconnectDelay} from '../utils/socket';

const MAX_RECONNECT_ATTEMPTS = 5;

interface UseReconnectOptions {
  maxAttempts?: number;
  onGiveUp?: () => void;
}

export function useReconnect({
  maxAttempts = MAX_RECONNECT_ATTEMPTS,
  onGiveUp,
}: UseReconnectOptions = {}) {
  const attemptsRef = useRef(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    attemptsRef.current = 0;
    cancel();
  }, [cancel]);

  const schedule = useCallback(
    (retry: () => void) => {
      if (attemptsRef.current >= maxAttempts) {
        onGiveUp?.();
        return false;
      }

      const attempt = (attemptsRef.current += 1);
      const delay = getReconnectDelay(attempt);
      console.warn(
        `Reconnecting to face detection socket (attempt ${attempt}/${maxAttempts}) in ${delay}ms`,
      );
      cancel();
      timerRef.current = setTimeout(retry, delay);
      return true;
    },
    [maxAttempts, onGiveUp, cancel],
  );

  useEffect(() => cancel, [cancel]);

  return {schedule, reset};
}
